import { useCallback } from 'react';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';

function extractResponseMessage(response) {
    if (!response) {
        return null;
    }

    if (typeof response === 'string') {
        return response;
    }

    return response.message || response.error || response.detail || null;
}

export const useDeleteHandler = ({
    service,
    entityName,
    selectedData,
    fetchData,
    setSelectedData,
    handleCloseModal,
    handleError,
    customFetchParams = {},
    deleteMethodName = 'delete',
    successMessage,
    errorMessage
}) => {
    const handleConfirmDelete = useCallback(async () => {
        if (!selectedData) {
            notifyError(`No ${entityName} selected for deletion.`);
            return;
        }

        try {
            const deleteMethod = service[`delete${entityName}`] || service[deleteMethodName];
            if (typeof deleteMethod !== 'function') {
                throw new Error(`Delete method not found for ${entityName}`);
            }
            
            // Extract ID - handle both cases: when selectedData is an object or just an ID
            const id = typeof selectedData === 'object' && selectedData !== null 
                ? selectedData.id 
                : selectedData;
            
            const response = await deleteMethod.call(service, id);
            fetchData(service[`get${entityName}s`] || service.getAll, customFetchParams);

            setSelectedData(null);
            handleCloseModal();
            notifySuccess(
                extractResponseMessage(response)
                || successMessage
                || `${entityName} deleted successfully`
            );
        } catch (error) {
            if (handleError) {
                handleError(`Failed to delete ${entityName}`, error);
            } else {
                const errorMsg = error.response?.data?.message || errorMessage || `Failed to delete ${entityName}`;
                notifyError(errorMsg);
                console.error(`Error deleting ${entityName}:`, error);
            }
        }
    }, [selectedData, fetchData, handleCloseModal, entityName, service, handleError, deleteMethodName, successMessage, errorMessage]);

    return handleConfirmDelete;
};